import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { formatDate, formatMoney } from '@/lib/format';
import { useMatches, useReserveUnit, type MatchRow } from './useInventory';

/**
 * The score, shown as the thing it is made of (#22).
 *
 * The acceptance criterion is an EXPLAINABLE score, so the bars are not decoration:
 * a rep on the phone needs to say "this one fits your budget and area but it is one
 * bedroom short", and a manager needs to be able to look at the weighting and
 * disagree with it. A single number tells you neither.
 */
function ScoreBreakdown({ breakdown }: { breakdown: MatchRow['breakdown'] }) {
  const { t } = useTranslation();
  const order = ['budget', 'area', 'bedrooms', 'down_payment', 'delivery', 'unit_type'];

  return (
    <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 sm:grid-cols-3">
      {order.map((key) => {
        const part = breakdown[key];
        if (!part) return null;
        const pct = part.max === 0 ? 0 : Math.round((part.score / part.max) * 100);

        return (
          <div key={key} className="flex items-center gap-1.5">
            <dt className="w-20 shrink-0 truncate text-[11px] text-slate-500">
              {t(`matching.${key}` as 'matching.budget')}
            </dt>
            <dd className="flex flex-1 items-center gap-1.5">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${
                    pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="w-10 shrink-0 text-[11px] tabular-nums text-slate-500" dir="ltr">
                {part.score}/{part.max}
              </span>
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function MatchingUnitsPanel({ leadId, revision }: { leadId: string; revision?: string }) {
  const { t } = useTranslation();
  const { data, isPending, isError } = useMatches(leadId, revision);
  const reserve = useReserveUnit();

  if (isPending) return <Spinner />;
  if (isError) return <Alert tone="error">{t('common.error')}</Alert>;

  const matches = data ?? [];

  if (matches.length === 0) {
    return <p className="text-sm text-slate-500">{t('matching.none')}</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-slate-500">{t('matching.intro')}</p>

      {matches.map((m) => (
        <article key={m.unit_id} className="rounded-xl border border-slate-200 p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold text-slate-900">
                {m.project_name} · {m.unit_code}
              </h3>
              <p className="text-xs text-slate-500">
                {m.developer_name} · {t(`unitTypes.${m.unit_type}` as 'unitTypes.apartment')}
                {m.bedrooms !== null ? ` · ${t('matching.beds', { count: m.bedrooms })}` : ''}
                {m.area_sqm ? ` · ${m.area_sqm} م²` : ''}
              </p>
            </div>

            <div className="text-end">
              <div className="text-sm font-semibold text-slate-900" dir="ltr">
                {formatMoney(m.price_total)}
              </div>
              {m.down_payment ? (
                <div className="text-xs text-slate-500" dir="ltr">
                  {t('matching.down')} {formatMoney(m.down_payment)}
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                m.score >= 85
                  ? 'bg-green-100 text-green-900'
                  : m.score >= 60
                    ? 'bg-amber-100 text-amber-900'
                    : 'bg-slate-100 text-slate-700'
              }`}
            >
              {t('matching.score', { score: m.score })}
            </span>

            <div className="flex items-center gap-2">
              {m.delivery_date ? (
                <span className="text-xs text-slate-500">
                  {t('matching.delivery')} {formatDate(m.delivery_date)}
                </span>
              ) : null}
              {/* A provisional hold, not the deal. The deal is opened from the
                  Deals panel, which also accepts a unit this lead already holds —
                  calling both of them "حجز" is how a rep books the wrong one. */}
              <Button
                variant="secondary"
                title={t('matching.reserveHint')}
                loading={reserve.isPending}
                onClick={() => reserve.mutate({ unitId: m.unit_id, leadId })}
              >
                {t('matching.reserve')}
              </Button>
            </div>
          </div>

          <ScoreBreakdown breakdown={m.breakdown} />
        </article>
      ))}

      {reserve.isError ? (
        <Alert tone="error">
          {reserve.error instanceof Error ? reserve.error.message : t('common.error')}
        </Alert>
      ) : null}
    </div>
  );
}
